import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import {
  BatchGetDescendantsForEntityUrnsOptions,
  GetConsumersRequest,
  GetConsumersResponse,
  GetDataContractRequest,
  GetDataContractResponse,
  GetDescendantsForEntityUrnsRequest,
  MarketplaceApi,
  RequestOptions,
} from './api';
import crossFetch from 'cross-fetch';
import {
  encodeQueryParams,
  handleFailedResponse,
} from '@agilelab/plugin-wb-platform-common';
import { EntityWithConsumableDescendants } from './types';

/**
 * A frontend and backend compatible client for communicating with the Marketplace
 * service.
 *
 * @public
 */
export class MarketplaceClient implements MarketplaceApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly baseUrlPromise: Promise<string>;

  constructor(options: {
    discoveryApi: DiscoveryApi;
    fetchApi?: { fetch: typeof fetch };
  }) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi || { fetch: crossFetch };
    this.baseUrlPromise = this.discoveryApi.getBaseUrl('marketplace');
  }

  async getBaseUrl() {
    return this.baseUrlPromise;
  }

  async instanceHasDocs(instanceId: string): Promise<boolean> {
    const baseUrl = await this.baseUrlPromise;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/documentation/static/${encodeURIComponent(instanceId)}`,
      {
        method: 'HEAD',
      },
    );
    return response.status === 200;
  }

  async getInstanceDocs(instanceId: string, path: string): Promise<string> {
    const baseUrl = await this.baseUrlPromise;

    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/documentation/static/${encodeURIComponent(
        instanceId,
      )}/${encodeURIComponent(path)}`,
      {
        method: 'GET',
      },
    );

    await handleFailedResponse(response);
    return response.text();
  }

  async rerouteStaticAssetBaseUrl(
    oldBaseUrl: string,
    instanceId: string,
    path: string,
  ): Promise<string> {
    const baseUrl = await this.baseUrlPromise;

    const newBaseUrl = `${baseUrl}/v1/documentation/static/${encodeURIComponent(
      instanceId,
    )}/${path}`;

    const newUrl = new URL(
      oldBaseUrl,
      newBaseUrl.endsWith('/') ? newBaseUrl : `${newBaseUrl}/`,
    ).toString();

    return newUrl;
  }

  async getConsumableDescendantsForEntityUrns(
    request: GetDescendantsForEntityUrnsRequest,
    options?: BatchGetDescendantsForEntityUrnsOptions,
  ): Promise<EntityWithConsumableDescendants[]> {
    const { entityUrns, environment } = request;
    const baseUrl = await this.baseUrlPromise;
    const chunkSize = options?.maxItemsPerRequest || 1000;

    const chunks = [];
    for (let i = 0; i < entityUrns.length; i += chunkSize) {
      chunks.push(entityUrns.slice(i, i + chunkSize));
    }

    const results = await Promise.all(
      chunks.map(async chunk => {
        const response = await this.fetchApi.fetch(
          `${baseUrl}/descendantsByUrns`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              ...(options?.token && {
                Authorization: `Bearer ${options.token}`,
              }),
            },
            body: JSON.stringify({
              entityUrns: chunk,
              environment,
            }),
          },
        );
        await handleFailedResponse(response);
        return response.json() as Promise<EntityWithConsumableDescendants[]>;
      }),
    );

    return results.flat();
  }

  async getConsumers(
    request: GetConsumersRequest,
    options?: RequestOptions,
  ): Promise<GetConsumersResponse> {
    const baseUrl = await this.baseUrlPromise;
    const queryParams = {
      environment: request.environment,
      includeChildren: request.includeChildren,
    };
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/consumers/projects/reading-from/${encodeURIComponent(
        request.externalId,
      )}${encodeQueryParams(queryParams)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options?.token}` }),
        },
      },
    );
    await handleFailedResponse(response);
    return response.json();
  }

  async getDataContract(
    request: GetDataContractRequest,
    options?: RequestOptions,
  ): Promise<GetDataContractResponse | undefined> {
    const baseUrl = await this.baseUrlPromise;
    const queryParams = {
      environment: request.identifier.environment,
    };
    const response = await this.fetchApi.fetch(
      `${baseUrl}/v1/data-contracts/${encodeURIComponent(
        request.identifier.externalId,
      )}${encodeQueryParams(queryParams)}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(options?.token && { Authorization: `Bearer ${options?.token}` }),
        },
      },
    );
    if (response.status === 404) return undefined;
    await handleFailedResponse(response);
    return response.json();
  }
}
